import React, { ReactElement, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Hidden, IconButton, MenuItem, Select, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DownloadIcon from "@mui/icons-material/Download";
import { toggleNavCollapsed } from "../../redux/actions/Setting";
import { fontColor } from "../../constants/ColorSets";
import { theme } from "../../themes/theme";
import { AppState } from "../../redux/store";
import { TeamMembersState } from "../../types/models/TeamMembersState";
import { Fonts } from "../../constants/AppEnums";
import {
  isCurrentAccountOrgManagerOrAdmin,
  isKiwiRailUser,
} from "../../components/AppBase/AppAuthProvider";
import { MyTeamId } from ".";
import { RuleUnitState } from "../../types/models/RuleUnitState";
import LoadingButton from "../../components/LoadingButton";

interface TeamPageHeaderProp {
  getDataForDownload: () => void;
  teamName: string | undefined;
  title: string;
  action?: ReactElement;
  onSelectTeam: (teamId: string) => void;
  isDownloading: boolean;
  loading: boolean;
  csvLinkRef: React.RefObject<unknown>;
}

const YourTeamHeader: React.FC<TeamPageHeaderProp> = (prop) => {
  const { team_id } = useParams();
  const dispatch = useDispatch();
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const isOrgManager = isCurrentAccountOrgManagerOrAdmin();
  const havingOtherMemberProgress = isKiwiRailUser(); // A KiwiRail user can have progress data even if they are not an OrgManager and not a leader

  useEffect(() => {
    if (!teamState.myTeams) return;
    let nextTeamId: string | undefined;

    if (team_id) {
      if (team_id == MyTeamId && (isOrgManager || havingOtherMemberProgress)) {
        nextTeamId = MyTeamId;
      } else {
        const team = teamState.myTeams?.find((team) => team.TeamId === team_id);
        if (team) {
          // Select team if its id is specified in URL
          nextTeamId = team_id;
        }
      }
    } else {
      // Initial selection
      if (isOrgManager || havingOtherMemberProgress) {
        nextTeamId = MyTeamId;
      } else {
        if (teamState.myTeams && teamState.myTeams.length > 0) {
          nextTeamId = teamState.myTeams[0].TeamId;
        }
      }
    }

    // Call change event if the selected team is changed
    if (selectedTeamId !== nextTeamId) {
      setSelectedTeamId(nextTeamId || "");
      if (nextTeamId) {
        prop.onSelectTeam(nextTeamId);
      }
    }
  }, [teamState.myTeams, team_id, ruleUnitState.userShieldId]);

  const handleChange = (teamId: string) => {
    setSelectedTeamId(teamId);
    prop.onSelectTeam(teamId);
  };

  const getTeamsMenus = () => {
    const menus: ReactElement[] = [];
    // A KiwiRail user can have progress data even if they are not an OrgManager and not a leader
    // In this case, we will show My Team to display the results.
    if (isOrgManager || havingOtherMemberProgress)
      menus.push(
        <MenuItem key={MyTeamId} value={MyTeamId}>
          <Box sx={{ fontWeight: Fonts.SEMI_BOLD }}>Org Team</Box>
        </MenuItem>
      );

    teamState.myTeams?.forEach((team) => {
      menus.push(
        <MenuItem key={team.TeamId} value={team.TeamId}>
          {team.DisplayName + (team.StartDate ? ` (${team.StartDate} - ${team.EndDate})` : "")}
        </MenuItem>
      );
    });
    return menus;
  };

  return (
    <Box sx={{ borderRadius: 0 }}>
      <Box sx={{ mt: 2, ml: 1.5 }}>
        <Select
          sx={{
            color: theme.palette.primary.main,
            fontWeight: Fonts.BOLD,
            ":before": { borderBottomColor: "transparent" },
            ":after": { borderBottomColor: "transparent" },
            "&&:hover::before": { borderBottomColor: theme.palette.primary.main },
          }}
          variant="standard"
          value={selectedTeamId}
          onChange={(event) => handleChange(event.target.value)}
        >
          {getTeamsMenus()}
        </Select>
      </Box>
      <Box
        sx={{
          height: 40,
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <Hidden lgUp>
            <IconButton
              sx={{
                color: "text.secondary",
                marginLeft: 1,
                padding: 0,
              }}
              edge="start"
              className="menu-btn"
              color="inherit"
              aria-label="open drawer"
              onClick={() => dispatch(toggleNavCollapsed())}
              size="large"
            >
              <MenuIcon
                sx={{
                  width: 35,
                  height: 35,
                }}
              />
            </IconButton>
          </Hidden>
          <Box sx={{ py: 1 }}>
            <Typography
              variant="h3"
              component="h3"
              color={fontColor.titleFontColor}
              style={{ fontSize: 22, marginLeft: 12 }}
            >
              {prop.title}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mr: 1 }}>
          <Hidden smDown>
            <Box sx={{ display: "flex" }}>
              <LoadingButton
                disabled={prop.loading}
                sx={{ width: 150 }}
                onClick={prop.getDataForDownload}
                loading={prop.isDownloading}
                icon={<DownloadIcon />}
              >
                {prop.isDownloading ? "Downloading..." : "Download CSV"}
              </LoadingButton>
            </Box>
          </Hidden>

          <Hidden smUp>
            <Box>
              <LoadingButton
                sx={{ width: 30 }}
                disabled={prop.loading}
                loading={prop.isDownloading}
                onClick={prop.getDataForDownload}
                icon={<DownloadIcon sx={{ color: theme.palette.primary.main, ml: 1 }} />}
              ></LoadingButton>
            </Box>
          </Hidden>
        </Box>
        <Box>
          <Box sx={{ mr: 0, mt: 2, display: "flex", justifyContent: "flex-end" }}>
            {prop.action}
          </Box>
        </Box>
      </Box>
      <Box sx={{ borderBottom: "1px solid lightgrey", mx: 1.5 }}></Box>
    </Box>
  );
};

export default YourTeamHeader;
